import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { mobileLogin } from '@/app/user';
import { Message } from '@arco-design/web-react';
import RtcClient from '@/lib/RtcClient';

interface AuthContextType {
  isLoggedIn: boolean;
  user: any | null;
  login: (phone: string, code: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    // 检查本地存储中是否有token和用户信息
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('userInfo');

    if (storedToken && storedUser) {
      try {
        // 解析用户信息
        const userData = JSON.parse(storedUser);

        // 验证token是否有效（这里简单判断是否存在，实际项目中应验证token有效性）
        if (storedToken === userData.token) {
          setUser(userData);
          setIsLoggedIn(true);
        } else {
          // token不匹配，清除存储的信息
          localStorage.removeItem('userInfo');
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('解析用户信息失败:', error);
        localStorage.removeItem('userInfo');
        localStorage.removeItem('token');
      }
    }

    setLoading(false);
  }, []);

  // 登录函数
  const login = async (phone: string, code: string): Promise<boolean> => {
    try {
      const result = await mobileLogin(phone, code);
      if (result.code === 200 && result.data?.userinfo) {
        const userData = result.data.userinfo;
        setUser(userData);
        setIsLoggedIn(true);
        return true;
      } else {
        Message.error(result.msg || '登录失败');
        return false;
      }
    } catch (error) {
      console.error('登录失败:', error);
      Message.error('登录失败，请稍后重试');
      return false;
    }
  };

  // 登出函数
  const logout = () => {
    RtcClient.leaveRoom()
    setUser(null);
    setIsLoggedIn(false);
    localStorage.removeItem('userInfo');
    localStorage.removeItem('token');
    navigate('/login');
  };

  // 如果正在加载，可以显示加载状态或者空内容
  if (loading) {
    return null; // 或者返回加载指示器
  }

  return <AuthContext.Provider value={{ isLoggedIn, user, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
